import { useIntl } from 'react-intl';
import { useMemo } from 'react';

const getLocalizedText = (mlp, intl) => {
  if (!mlp || !Array.isArray(mlp) || mlp.length === 0) return '';

  const locale = (intl.locale || 'en').split('-')[0].toLowerCase();
  const match = mlp.find(item => item.language?.toLowerCase() === locale);
  const fallback = match || mlp.find(item => item.language?.toLowerCase() === 'en') || mlp[0];
  return fallback?.text?.trim() || '';
};

const findByIdShort = (elements = [], idShort) =>
  elements.find(el => el.idShort === idShort);

// Helper: safely extract numbered elements by prefix (e.g. "DocumentVersion01")
const findAllByIdShortPrefix = (elements = [], prefix) => {
  if (!Array.isArray(elements)) return [];

  return elements
    .map(el => {
      // Safety first
      if (!el || typeof el.idShort !== 'string') return null;

      // Must start exactly with prefix followed only by digits
      const match = el.idShort.match(new RegExp(`^${prefix}(\\d+)$`));
      if (!match) return null;

      const number = parseInt(match[1], 10);
      if (isNaN(number)) return null;

      return { element: el, number };
    })
    .filter(Boolean); // removes all null/undefined/falsy entries → 100% safe now
};

// Get the latest (highest number) element for a given prefix
const getLatestByIdShortPrefix = (elements, prefix) => {
  const matches = findAllByIdShortPrefix(elements, prefix);
  if (matches.length === 0) return null;

  // Safe sort – all items here have valid .number
  matches.sort((a, b) => b.number - a.number);
  return matches[0].element;
};

const extractDocument = (collectionItem, intl) => {
  if (collectionItem.modelType !== 'SubmodelElementCollection') return null;

  // Category from top-level displayName
  const category = getLocalizedText(collectionItem.displayName, intl) || 'Uncategorized';

  // Find the latest DocumentVersionXX
  const latestVersionCol = getLatestByIdShortPrefix(collectionItem.value, 'DocumentVersion');
  if (!latestVersionCol?.value) return null;

  const v = latestVersionCol.value;

  const titleMlp = findByIdShort(v, 'Title');
  const subtitleMlp = findByIdShort(v, 'SubTitle');
  const languageProp = findByIdShort(v, 'Language');
  const dateProp = findByIdShort(v, 'StatusSetDate');
  const organizationOfficialProp = findByIdShort(v, 'OrganizationOfficialName');

  const digitalFiles = findAllByIdShortPrefix(v, 'DigitalFile')
    .sort((a, b) => a.number - b.number) // optional: keep order 01, 02, 03...
    .map(match => match.element)
    .map(el => ({
      url: el?.value || null,
      hasUrl: !!el?.value?.startsWith('http'),
      // Optional: you can add filename from semanticId or description later if needed
    }))
    .filter(f => f.url); // only keep ones that actually have a URL

  const title = titleMlp ? getLocalizedText(titleMlp.value, intl) : '';
  if (!title) return null;

  const subtitle = subtitleMlp ? getLocalizedText(subtitleMlp.value, intl) : null;

  return {
    category,
    title,
    subtitle: subtitle && subtitle.trim() ? subtitle.trim() : null,
    language: languageProp?.value || 'EN',
    organizationOfficial: organizationOfficialProp?.value || null,
    date: dateProp?.value || null,
    digitalFiles,
    hasPdf: digitalFiles.length > 0 && digitalFiles.some(f => f.hasUrl)
  };
};

export const useHandoverDocuments = (submodel) => {
  const intl = useIntl();

  const { allDocuments, categories } = useMemo(() => {
    if (!submodel?.submodelElements) {
      return { allDocuments: [], categories: [] };
    }

    const docs = [];
    const categorySet = new Set();

    submodel.submodelElements.forEach(item => {
      const doc = extractDocument(item, intl);
      if (doc) {
        docs.push(doc);
        categorySet.add(doc.category);
      }
    });

    return {
      allDocuments: docs,
      categories: Array.from(categorySet).sort(), // optional sorting
    };
  }, [submodel, intl]);

  const hasDocuments = allDocuments.length > 0;

  return {
    allDocuments,     // flat array of all real documents
    categories,       // just for potential UI use (e.g. badges)
    hasDocuments,
  };
};